// src/components/AcceptInvitation.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import VelvetLogo from '../icons/VL_WordmarkOnly_Light.svg';
import '../styles/AcceptInvitation.css';

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authCheckAttempts, setAuthCheckAttempts] = useState(0);
  const [debugInfo, setDebugInfo] = useState('');
  const [hasReturnedFromOAuth, setHasReturnedFromOAuth] = useState(false);
  const [alreadyLoggedInError, setAlreadyLoggedInError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link - no token provided');
      setLoading(false);
      return;
    }

    validateInvitation();

    // Check if user just returned from OAuth
    const hasJustCompletedOAuth = checkIfReturnedFromOAuth();

    if (hasJustCompletedOAuth) {
      setHasReturnedFromOAuth(true);
      setDebugInfo('Detected return from OAuth, checking authentication...');
      checkAuthStatus();
    } else {
      setDebugInfo('Initial invitation page load - checking if already authenticated...');
      // Check if user might already be authenticated (persistent session)
      checkAuthStatus();
    }
  }, [token]);

  // Only retry auth checks if we've returned from OAuth
  useEffect(() => {
    if (hasReturnedFromOAuth && !isAuthenticated && authCheckAttempts < 5) {
      const delays = [500, 1500, 3000, 5000, 7000];
      const delay = delays[authCheckAttempts] || 7000;
      
      const timer = setTimeout(() => {
        setDebugInfo(`Auth retry attempt ${authCheckAttempts + 1} after ${delay}ms delay...`);
        setAuthCheckAttempts(prev => prev + 1);
        checkAuthStatus();
      }, delay);
      
      return () => clearTimeout(timer);
    }
  }, [hasReturnedFromOAuth, isAuthenticated, authCheckAttempts]);

  const checkIfReturnedFromOAuth = () => {
    // Check various indicators that user just completed OAuth
    const referrer = document.referrer;
    const hasSessionStorage = !!sessionStorage.getItem('oauth_in_progress');
    const hasGoogleReferrer = referrer.includes('accounts.google.com');
    const hasMicrosoftReferrer = referrer.includes('login.microsoftonline.com') || referrer.includes('microsoft.com');

    // Clear the oauth indicator if it exists
    sessionStorage.removeItem('oauth_in_progress');

    return hasGoogleReferrer || hasMicrosoftReferrer || hasSessionStorage;
  };

  const validateInvitation = async () => {
    try {
      console.log('Validating invitation token:', token);
      const response = await axios.get(`/api/invitation/${token}`);
      console.log('Invitation validated:', response.data);
      setInvitation(response.data);
    } catch (error) {
      console.error('Error validating invitation:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      if (error.response?.status === 404) {
        setError('This invitation is invalid or has expired');
      } else {
        setError('Failed to validate invitation');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkAuthStatus = async () => {
    try {
      setDebugInfo(`Checking authentication... (attempt ${authCheckAttempts + 1})`);
      
      const response = await axios.get('/api/user/profile', {
        timeout: 10000,
        withCredentials: true
      });
      
      // Check if this is an invitation flow user
      if (response.data.invitation_flow) {
        setIsAuthenticated(true);
        setDebugInfo('✓ Authenticated for invitation flow');
        console.log('User authenticated for invitation:', response.data);
        return true;
      }
      
      // Normal authenticated user
      if (response.data.authenticated) {
        setIsAuthenticated(true);
        setDebugInfo('✓ Authenticated successfully');
        console.log('User authenticated:', response.data);
        return true;
      }
      
      // Shouldn't reach here, but handle as not authenticated
      setIsAuthenticated(false);
      setDebugInfo('✗ Unexpected response format');
      return false;
      
    } catch (error) {
      setIsAuthenticated(false);
      const status = error.response?.status;
      const message = error.response?.data?.error || error.message;
      
      setDebugInfo(`✗ Auth failed: ${status || 'Network'} - ${message}`);
      console.log('Auth check failed:', {
        status,
        message,
        attempts: authCheckAttempts + 1
      });
      return false;
    }
  };

  const handleGoogleLogin = () => {
    // Mark that OAuth is in progress
    sessionStorage.setItem('oauth_in_progress', 'true');

    // Clear debug info and reset attempts
    setDebugInfo('Redirecting to Google login...');
    setAuthCheckAttempts(0);

    // Redirect to Google login with invitation token as parameter
    const isProduction = window.location.hostname !== 'localhost';
    const loginUrl = isProduction
      ? `${window.location.origin}/auth/login?invitation_token=${token}`
      : `http://localhost:5001/auth/login?invitation_token=${token}`;

    console.log('Redirecting to:', loginUrl);
    window.location.href = loginUrl;
  };

  const handleMicrosoftLogin = () => {
    // Mark that OAuth is in progress
    sessionStorage.setItem('oauth_in_progress', 'true');

    // Clear debug info and reset attempts
    setDebugInfo('Redirecting to Microsoft login...');
    setAuthCheckAttempts(0);

    // Redirect to Microsoft login with invitation token as parameter
    const isProduction = window.location.hostname !== 'localhost';
    const loginUrl = isProduction
      ? `${window.location.origin}/auth/microsoft/login?invitation_token=${token}`
      : `http://localhost:5001/auth/microsoft/login?invitation_token=${token}`;

    console.log('Redirecting to:', loginUrl);
    window.location.href = loginUrl;
  };

  const handleLogout = async () => {
    try {
      await axios.get('/auth/logout');
      // After logout, reset state and reload the page to show login button
      setAlreadyLoggedInError(null);
      setIsAuthenticated(false);
      setError(null);
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
      // Force reload even if logout fails
      window.location.reload();
    }
  };

  const handleAcceptInvitation = async () => {
    setProcessing(true);
    setError(null);
    setDebugInfo('Processing invitation acceptance...');

    try {
      const response = await axios.post('/api/accept-invitation', {
        token: token
      }, {
        withCredentials: true,
        timeout: 15000
      });

      if (response.data.success) {
        setDebugInfo('✓ Account created successfully! Redirecting...');
        
        // Success! Determine redirect URL
        const user = response.data.user;
        let redirectUrl;
        
        if (user.is_admin) {
          redirectUrl = '/admin-dashboard';
        } else if (user.is_operator) {
          redirectUrl = '/operator-dashboard';
        } else if (user.entity_type === 'client') {
          redirectUrl = '/client-dashboard';
        } else if (user.entity_type === 'organization') {
          redirectUrl = '/organization-dashboard';
        } else if (user.entity_type === 'brand') {
          redirectUrl = '/brand-dashboard';
        } else {
          redirectUrl = '/';
        }
        
        // Use window.location.href instead of React Router navigate
        // This forces a page refresh which ensures the new session is properly loaded
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 1000); // Small delay to show success message
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);

      if (error.response?.data?.error === 'already_logged_in') {
        // User is already logged in as an existing account
        setAlreadyLoggedInError(error.response.data);
        setDebugInfo('✗ Already logged in as existing user');
      } else if (error.response?.data?.action === 'redirect_to_login') {
        setError('Please sign in with Google first');
        setIsAuthenticated(false);
        setHasReturnedFromOAuth(false);
        setAuthCheckAttempts(0);
      } else {
        const errorMessage = error.response?.data?.error || 'Failed to accept invitation';
        setError(errorMessage);
      }
      setDebugInfo('✗ Failed to accept invitation');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="invitation-container">
        <div className="invitation-card">
          <div className="loading-spinner"></div>
          <p>Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (alreadyLoggedInError) {
    return (
      <div className="invitation-container">
        <div className="invitation-card error">
          <div className="invitation-header">
            <img src={VelvetLogo} alt="Velvet" className="logo" />
            <p className="subtitle">Platform Management</p>
          </div>
          <h2>Already Logged In</h2>
          <p>{alreadyLoggedInError.message}</p>
          <div className="logged-in-user-info">
            <p className="user-info-label">Currently logged in as:</p>
            <p className="user-info-name">
              {alreadyLoggedInError.current_user?.name}
            </p>
            <p className="user-info-email">
              {alreadyLoggedInError.current_user?.email}
            </p>
          </div>
          <div className="invitation-actions">
            <button
              className="btn-primary"
              onClick={handleLogout}
            >
              Log Out and Continue
            </button>
            <button
              className="btn-secondary"
              onClick={() => navigate('/')}
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="invitation-container">
        <div className="invitation-card">
          <div className="invitation-header">
            <img src={VelvetLogo} alt="Velvet" className="logo" />
            <p className="subtitle">Platform Management</p>
          </div>
          <div style={{ textAlign: 'center', padding: '30px 20px' }}>
            <p style={{ fontSize: '16px', color: 'var(--color-text-primary)', margin: '0' }}>
              This invitation is invalid or has expired.
            </p>
          </div>
          <div className="invitation-actions">
            <button
              className="btn-primary"
              onClick={() => navigate('/')}
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="invitation-container">
      <div className="invitation-card">
        <div className="invitation-header">
          <img src={VelvetLogo} alt="Velvet" className="logo" />
          <p className="subtitle">Platform Management</p>
          <h1>You're Invited</h1>
        </div>

        <div className="invitation-details">
          <div className="invitation-info">
            <p>You've been invited to join Velvet as a <strong>{invitation.user_type.replace('_', ' ').toUpperCase()}</strong>.</p>
            <p className="email-info">Your account will be linked to <strong>{invitation.email}</strong></p>
          </div>

          <div className="expiration-notice">
            This invitation expires on {new Date(invitation.expires_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        </div>

        <div className="invitation-actions">
          {!isAuthenticated ? (
            <div className="auth-required">
              <p>Choose your sign-in method to get started</p>
              <button
                className="google-login-btn"
                onClick={handleGoogleLogin}
              >
                <div className="google-icon">
                  <svg width="18" height="18" viewBox="0 0 18 18">
                    <path
                      fill="#4285F4"
                      d="M9 8.182v2.727h4.636c-.201 1.636-1.636 2.727-4.636 2.727-2.727 0-4.909-2.182-4.909-4.909S6.273 4.364 9 4.364c1.227 0 2.318.409 3.182 1.182l2.045-2.045C12.682 2.045 10.955 1.364 9 1.364 4.636 1.364 1.091 4.909 1.091 9.227s3.545 7.864 7.909 7.864c4.5 0 7.636-3.182 7.636-7.636 0-.545-.055-1.091-.164-1.636H9z"
                    />
                  </svg>
                </div>
                <span>Sign in with Google</span>
              </button>

              <div className="login-divider">
                <span>OR</span>
              </div>

              <button
                className="microsoft-login-btn"
                onClick={handleMicrosoftLogin}
              >
                <div className="microsoft-icon">
                  <svg width="21" height="21" viewBox="0 0 21 21">
                    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                  </svg>
                </div>
                <span>Sign in with Microsoft / Teams</span>
              </button>
            </div>
          ) : (
            <div className="accept-invitation">
              <button
                className="btn-accept"
                onClick={handleAcceptInvitation}
                disabled={processing}
              >
                {processing ? (
                  <>
                    <div className="btn-spinner"></div>
                    Creating your account...
                  </>
                ) : (
                  'Accept Invitation'
                )}
              </button>
            </div>
          )}
        </div>

        <div className="invitation-footer">
          <p>Need help? Contact the person who invited you</p>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;